import { useState, useEffect, JSX } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Card,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import BackgroundImage from "../assets/background-1.jpg";
import AgentAVatar from "../assets/call-agent.png";
import { useSessionCall, useSIPProvider } from "../../sip/SIPProviderContext";
import useCustomMediaQuery from "../../shared/mediaQuery/useCustomMediaQuery";
import { last } from "lodash";
import { useNavigate } from "react-router-dom";
import { CONNECT_STATUS, RegisterStatus } from "../../sip/type";
import { SessionState } from "sip.js";
import { useLocation } from "react-router-dom";
import { isAppRunningOnMobile } from "../../shared/utils/platform";

const OutboundingCall = (): JSX.Element | null => {
  const {
    isExtraSmall,
    isSmall: isSmallScreen,
    isMedium,
  } = useCustomMediaQuery();
  const isXSmall = isExtraSmall;
  const isSmall = isSmallScreen || isMedium;
  const [callTime, setCallTime] = useState<number>(0);
  const [isLoudSpeaker, setIsLoudSpeaker] = useState(false);
  const navigate = useNavigate();
  const {
    sessions,
    sessionTimer,
    sessionManager,
    registerStatus,
    connectStatus,
  } = useSIPProvider();
  const sessionId = last(Object.keys(sessions)) ?? "";
  const call = useSessionCall(sessionId);
  const location = useLocation();
  const { selectedCompany } = location.state || {};
  const isMobile = isAppRunningOnMobile();

  // Get reference to remote audio element
  const getAudioElement = () =>
    document.getElementById("remoteAudio") as HTMLAudioElement;

  // Handle audio routing changes
  const handleAudioRouting = async (useSpeaker: boolean) => {
    const audioElement = getAudioElement();
    if (audioElement?.setSinkId) {
      try {
        await audioElement.setSinkId(useSpeaker ? "" : "default");
        setIsLoudSpeaker(useSpeaker);
      } catch (error) {
        console.error("Error changing audio output:", error);
      }
    }
  };

  // Set initial audio output for mobile devices
  useEffect(() => {
    if (isMobile) {
      handleAudioRouting(false);
    }
  }, []);

  useEffect(() => {
    if (!call) navigate("/dashboard", { replace: true });
  }, [call, sessions]);

  useEffect(() => {
    if (
      connectStatus !== CONNECT_STATUS.CONNECTED &&
      registerStatus !== RegisterStatus.REGISTERED
    ) {
      navigate("/dashboard", { replace: true });
    }
  }, [registerStatus, connectStatus]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCallTime((prevTime) => prevTime + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (!call) return null;

  const { isMuted, isHeld, session, hangup, mute, unmute, hold, unhold } = call;
  const timerValue = sessionTimer[sessionId];
  const isAnswered = session?.state === SessionState.Established;

  const formatTime = (totalSeconds: number): string => {
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleMuteCall = () => (isMuted ? unmute() : mute());
  const handleHoldCall = () => (isHeld ? unhold() : hold());
  const handleHangupCall = () => {
    hangup();
  };

  const toggleSpeaker = () => handleAudioRouting(!isLoudSpeaker);

  const computedWidth = isSmall ? "80%" : "30%";
  const cardWidth = isXSmall ? "95%" : computedWidth;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "3%",
        left: 0,
        width: "100%",
        height: "100%",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - -43px)",
        }}
      >
        <Card
          sx={{
            width: cardWidth,
            height: "65vh",
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-evenly",
            alignItems: "center",
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            border: "1px solid #c5c9e1",
          }}
        >
          {isAnswered ? (
            <Typography variant="subtitle1">
              Connected to{" "}
              <span style={{ fontWeight: "bold", color: "#279f76" }}>
                {selectedCompany}'s
              </span>{" "}
              assistant...
            </Typography>
          ) : (
            <Typography variant="subtitle1">
              Calling{" "}
              <span style={{ fontWeight: "bold", color: "#279f76" }}>
                {selectedCompany}
              </span>
              ...
            </Typography>
          )}

          <Avatar sx={{ width: 150, height: 150 }} src={AgentAVatar} />
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
            {formatTime(callTime)}
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Control Rows Container */}
            <div className="w-full flex flex-col items-center gap-6">
              {/* First Row - Mute, Hold, Speaker */}
              <Stack
                direction="row"
                spacing={4}
                className="justify-center items-center w-full"
              >
                {/* Mute Button */}
                <IconButton
                  onClick={handleMuteCall}
                  className="rounded-full"
                  sx={{
                    backgroundColor: "#e5e7eb",
                    "&:hover": { backgroundColor: "#e5e7eb" },
                  }}
                >
                  {isMuted ? <MicOffIcon /> : <MicIcon />}
                </IconButton>

                {/* Hold Button */}
                <IconButton
                  onClick={handleHoldCall}
                  className="rounded-full"
                  sx={{
                    backgroundColor: "#e5e7eb",
                    "&:hover": { backgroundColor: "#e5e7eb" },
                  }}
                >
                  {isHeld ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>

                {/* Speaker Button */}
                <IconButton
                  onClick={toggleSpeaker}
                  className="rounded-full"
                  sx={{
                    backgroundColor: "#e5e7eb",
                    "&:hover": { backgroundColor: "#e5e7eb" },
                  }}
                >
                  {isLoudSpeaker ? <VolumeUpIcon /> : <VolumeOffIcon />}
                </IconButton>
              </Stack>

              {/* Second Row - End Call (Centered) */}
              <div className="w-full flex justify-center mt-4">
                <IconButton
                  onClick={handleHangupCall}
                  className="rounded-full"
                  sx={{
                    backgroundColor: "#fee2e2", // bg-red-100
                    "&:hover": { backgroundColor: "#fee2e2" },
                  }}
                >
                  <PhoneIcon sx={{ color: "#dc2626" }} /> {/* text-red-600 */}
                </IconButton>
              </div>
            </div>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default OutboundingCall;
